import { useEffect, useRef } from 'react';

export default function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let shapes = [];
    let waves = []; // Sound wave visualization

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create sound wave visualization
    const createWaves = () => {
      waves = [];
      const waveCount = 6; // Increased from 4 to 6
      const waveHeight = canvas.height / waveCount;
      
      for (let i = 0; i < waveCount; i++) {
        waves.push({
          y: (i + 0.5) * waveHeight,
          amplitude: 30 + Math.random() * 40,
          frequency: 0.01 + Math.random() * 0.02,
          phase: Math.random() * Math.PI * 2,
          speed: 0.5 + Math.random() * 1,
          opacity: 0.03 + Math.random() * 0.05, // Reduced intensity
          color: i % 2 === 0 
            ? `rgba(245, 51, 161, ${0.03 + Math.random() * 0.05})` 
            : `rgba(236, 72, 153, ${0.03 + Math.random() * 0.05})`,
        });
      }
    };

    const drawWave = (wave, time) => {
      ctx.save();
      ctx.globalAlpha = wave.opacity;
      ctx.strokeStyle = wave.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      for (let x = 0; x < canvas.width; x += 2) {
        const y = wave.y + Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude;
        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      ctx.stroke();
      ctx.restore();
    };

    // Create floating shapes - only balloons
    const createShapes = () => {
      shapes = [];
      const shapeCount = 8; // Fewer balloons
      
      for (let i = 0; i < shapeCount; i++) {
        const opacity = 0.06 + Math.random() * 0.10; // Reduced intensity
        // Varied sizes - some bigger, some smaller
        const sizeVariation = Math.random();
        const size = sizeVariation < 0.3 
          ? 40 + Math.random() * 30  // Small balloons (30%)
          : sizeVariation < 0.7
          ? 60 + Math.random() * 40  // Medium balloons (40%)
          : 90 + Math.random() * 50; // Large balloons (30%)
        
        shapes.push({
          x: Math.random() * canvas.width,
          y: canvas.height + size, // Start from bottom
          size: size,
          speedX: (Math.random() - 0.5) * 0.4, // Left-right movement (slower)
          speedY: -(0.3 + Math.random() * 0.4), // Always upward (negative = up)
          rotation: 0, // No rotation for balloons
          rotationSpeed: 0, // No rotation
          type: 'balloon',
          opacity: opacity,
          color: i % 3 === 0 
            ? `rgba(245, 51, 161, ${opacity})` // Primary pink
            : i % 3 === 1
            ? `rgba(236, 72, 153, ${opacity})` // Pink
            : `rgba(255, 42, 160, ${opacity})`, // Light pink
        });
      }
    };

    const drawShape = (shape) => {
      ctx.save();
      ctx.globalAlpha = shape.opacity;
      ctx.translate(shape.x, shape.y);
      // No rotation for balloons - they stay upright
      ctx.fillStyle = shape.color;
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = 2;

      // Draw balloon (oval shape with string)
      const width = shape.size;
      const height = shape.size * 1.3; // Taller than wide
      
      // Balloon body (oval)
      ctx.beginPath();
      ctx.ellipse(0, -height * 0.15, width / 2, height / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Balloon highlight (smaller oval for 3D effect)
      ctx.globalAlpha = shape.opacity * 0.6;
      ctx.beginPath();
      ctx.ellipse(-width * 0.15, -height * 0.25, width * 0.2, height * 0.25, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = shape.opacity;
      
      // String (thin line from bottom of balloon)
      ctx.beginPath();
      ctx.moveTo(0, height * 0.35);
      ctx.lineTo(0, height * 0.6);
      ctx.stroke();
      
      // String end (small knot)
      ctx.beginPath();
      ctx.arc(0, height * 0.6, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const time = Date.now() * 0.001;

      // Draw waves first (background layer)
      waves.forEach((wave) => {
        drawWave(wave, time);
      });

      // Draw shapes on top
      shapes.forEach((shape) => {
        // Update position
        shape.x += shape.speedX;
        shape.y += shape.speedY;
        // No rotation for balloons

        // Wrap around edges - balloons come from bottom when they go off top
        if (shape.x < -shape.size) shape.x = canvas.width + shape.size;
        if (shape.x > canvas.width + shape.size) shape.x = -shape.size;
        if (shape.y < -shape.size * 2) {
          // Reset to bottom when balloon goes off top
          shape.y = canvas.height + shape.size;
          shape.x = Math.random() * canvas.width; // Random horizontal position
        }

        drawShape(shape);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    createShapes();
    createWaves();
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}
